/**
 * Accessible form inputs with React Hook Form integration.
 *
 * ponytail: FormSelect and FormCheckbox used to live here too, with zero
 * callers anywhere in src/ — dead flexibility for fields nothing renders. Add
 * one back the day a form actually needs a select or a checkbox.
 */

import React, { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';

// ==========================================
// Types
// ==========================================

interface BaseFieldProps {
  label: string;
  error?: string;
  helperText?: string;
  required?: boolean;
}

// ==========================================
// Form Input
// ==========================================

interface FormInputProps extends InputHTMLAttributes<HTMLInputElement>, BaseFieldProps {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  ({ label, error, helperText, required, leftIcon, rightIcon, className = '', id, ...props }, ref) => {
    const inputId = id || `input-${label.toLowerCase().replace(/\s+/g, '-')}`;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    return (
      <div className="space-y-1.5">
        <label htmlFor={inputId} className="block text-sm font-bold text-ink">
          {label}
          {required && <span className="text-error-text ml-1" aria-hidden="true">*</span>}
        </label>

        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            aria-invalid={!!error}
            aria-describedby={error ? errorId : helperText ? helperId : undefined}
            aria-required={required}
            className={`
              w-full px-4 py-2.5 rounded-lg border-2 transition-colors duration-150 ease-out
              bg-white text-ink placeholder:text-muted
              focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2
              disabled:bg-surface-2 disabled:cursor-not-allowed
              ${leftIcon ? 'pl-10' : ''}
              ${rightIcon || error ? 'pr-12' : ''}
              ${error ? 'border-error-text' : 'border-line focus:border-ink'}
              ${className}
            `}
            {...props}
          />

          {error && !rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <AlertCircle className="w-5 h-5 text-error-text" aria-hidden="true" />
            </div>
          )}

          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {rightIcon}
            </div>
          )}
        </div>

        {error && (
          <p id={errorId} role="alert" className="text-sm text-error-text flex items-center gap-1">
            <AlertCircle className="w-4 h-4" aria-hidden="true" />
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={helperId} className="text-sm text-muted">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

FormInput.displayName = 'FormInput';

// ==========================================
// Password Input with Toggle
// ==========================================

type PasswordInputProps = Omit<FormInputProps, 'type' | 'rightIcon'>;

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (props, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);

    return (
      <FormInput
        ref={ref}
        type={showPassword ? 'text' : 'password'}
        rightIcon={
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="inline-flex h-11 w-11 items-center justify-center -mr-2 rounded-pill text-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2"
            aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        }
        {...props}
      />
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

// ==========================================
// Form Textarea
// ==========================================

interface FormTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, BaseFieldProps {}

export const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  ({ label, error, helperText, required, className = '', id, ...props }, ref) => {
    const textareaId = id || `textarea-${label.toLowerCase().replace(/\s+/g, '-')}`;
    const errorId = `${textareaId}-error`;
    const helperId = `${textareaId}-helper`;

    return (
      <div className="space-y-1.5">
        <label htmlFor={textareaId} className="block text-sm font-bold text-ink">
          {label}
          {required && <span className="text-error-text ml-1" aria-hidden="true">*</span>}
        </label>

        <textarea
          ref={ref}
          id={textareaId}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : helperText ? helperId : undefined}
          aria-required={required}
          className={`
            w-full px-4 py-2.5 rounded-lg border-2 transition-colors duration-150 ease-out
            bg-white text-ink placeholder:text-muted
            focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2
            disabled:bg-surface-2 disabled:cursor-not-allowed
            resize-none
            ${error ? 'border-error-text' : 'border-line focus:border-ink'}
            ${className}
          `}
          {...props}
        />

        {error && (
          <p id={errorId} role="alert" className="text-sm text-error-text flex items-center gap-1">
            <AlertCircle className="w-4 h-4" aria-hidden="true" />
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={helperId} className="text-sm text-muted">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

FormTextarea.displayName = 'FormTextarea';

export default FormInput;
